/**
 * AuthContext — F2 MVP.
 *
 * Trzyma stan zalogowanego admina. Sprawdza /api/auth/admin/me na mount.
 *
 * F5 dorzuci: tenant users (osobny kontekst albo discriminated union),
 * modules[], companies[], activeCompanyId, switchCompany, refresh rotation.
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import type { ThemePreset } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';

export type AdminRole = 'super_admin' | 'support' | 'finance' | 'readonly';

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  name: string | null;
  role: AdminRole;
  /** Preferencje UI zapisane na koncie (motyw kolorystyczny + tryb jasny/ciemny). */
  uiThemePreset: ThemePreset;
  uiThemeMode: 'light' | 'dark';
  /**
   * Kody uprawnień RBAC admina (suma po wszystkich rolach) z `GET /api/auth/admin/me`.
   * Frontend gatuje lokalnie UI (np. przyciski projektant vs opiekun w Kreatorze — hub
   * Krok 5). Pusta tablica gdy /me nie zdążyło albo profil bez ról.
   */
  permissions: string[];
}

interface AuthContextValue {
  admin: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Czy zalogowany admin ma dane uprawnienie (gating UI). false gdy brak admina. */
  hasPermission: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Sprawdz aktualna sesje (cookie) przy mount
  const refresh = async () => {
    try {
      const { data } = await api.get<AdminUser>('/auth/admin/me', { _skipAuthRefresh: true });
      setAdmin(data);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const login = async (email: string, password: string) => {
    await api.post('/auth/admin/login', { email, password });
    // Odpowiedź logowania ma okrojony profil (bez roli/uprawnień) — pełne dane wraz z
    // listą uprawnień bierzemy z /me, żeby gating UI (hub Krok 5) działał od razu po wejściu.
    await refresh();
  };

  const logout = useCallback(async () => {
    await api.post('/auth/admin/logout');
    setAdmin(null);
  }, []);

  const hasPermission = useCallback(
    (code: string) => admin?.permissions?.includes(code) ?? false,
    [admin],
  );

  return (
    <AuthContext.Provider value={{ admin, loading, login, logout, refresh, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
